import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BankAccountSelect } from "@/components/BankAccounts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Upload, FileText, Eye, Trash2, Printer, Landmark } from "lucide-react";

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
};
const fmtSize = (n) => (n ? `${(n / 1024).toFixed(0)} KB` : "");

export function PayrollStubs({ propertyId }) {
  const [stubs, setStubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [payDate, setPayDate] = useState("");
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  // MICR stamping
  const [micrOpen, setMicrOpen] = useState(false);
  const [micrStub, setMicrStub] = useState(null);
  const [micrStart, setMicrStart] = useState("");
  const [micrAccts, setMicrAccts] = useState([]);
  const [micrBankId, setMicrBankId] = useState(null);
  const [micrDx, setMicrDx] = useState("0");
  const [micrDy, setMicrDy] = useState("0");
  const [micrBusy, setMicrBusy] = useState(false);

  const load = useCallback(() => {
    if (!propertyId) return;
    setLoading(true);
    api.get("/payroll-stubs", { params: { property_id: propertyId } })
      .then((r) => setStubs(r.data))
      .catch(() => setStubs([]))
      .finally(() => setLoading(false));
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const openUpload = () => {
    setPayDate(new Date().toISOString().slice(0, 10));
    setLabel("");
    setFile(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!payDate) return toast.error("Choose the pay date");
    if (!file) return toast.error("Choose a PDF to upload");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/payroll-stubs", fd, {
        params: { property_id: propertyId, pay_date: payDate, label: label || "" },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Payroll stubs uploaded");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openFile = async (s) => {
    try {
      const res = await api.get(`/payroll-stubs/${s.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete payroll stubs for ${fmtDate(s.pay_date)}?`)) return;
    try {
      await api.delete(`/payroll-stubs/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openMicr = async (s) => {
    setMicrStub(s);
    setMicrDx("0");
    setMicrDy("0");
    setMicrStart("");
    setMicrOpen(true);
    try {
      const r = await api.get(`/properties/${propertyId}/bank-accounts`);
      setMicrAccts(r.data.accounts);
      const d = r.data.accounts.find((a) => a.id === r.data.default_bank_id);
      setMicrBankId(d?.id || null);
      setMicrStart(String(d?.current_check_number ?? 1001));
    } catch { /* leave blank -> backend default */ }
  };

  const runMicr = async (commit) => {
    const start = parseInt(micrStart, 10);
    if (!start || start < 1) return toast.error("Enter a valid starting check number");
    setMicrBusy(true);
    try {
      const res = await api.post(`/payroll-stubs/${micrStub.id}/micr`,
        { start_number: start, bank_id: micrBankId, dx_in: parseFloat(micrDx) || 0, dy_in: parseFloat(micrDy) || 0, commit: !!commit },
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      if (commit) {
        toast.success(`MICR checks generated starting #${start}`);
        setMicrOpen(false);
      } else {
        toast.success("Preview opened — adjust the offsets if the MICR line needs to move");
      }
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) { try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* noop */ } }
      toast.error(formatApiErrorDetail(detail));
    } finally {
      setMicrBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="payroll-stubs-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Upload the combined pay-stub PDF for each pay date (e.g. from QuickBooks), then view or print it here.
        </p>
        <Button onClick={openUpload} disabled={!propertyId} data-testid="upload-stub-btn">
          <Upload className="mr-2 h-4 w-4" /> Upload Payroll Stubs
        </Button>
      </div>

      <Card className="rounded-none border">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : stubs.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">No payroll stubs uploaded yet.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pay Date</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Uploaded By</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stubs.map((s) => (
                <TableRow key={s.id} data-testid={`stub-row-${s.id}`}>
                  <TableCell className="whitespace-nowrap font-semibold">{fmtDate(s.pay_date)}</TableCell>
                  <TableCell className="text-muted-foreground">{s.label || "—"}</TableCell>
                  <TableCell className="max-w-[18rem] truncate text-muted-foreground">
                    {s.file?.filename} <span className="text-xs">{fmtSize(s.file?.size)}</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{s.uploaded_by || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openFile(s)} data-testid={`view-stub-${s.id}`}>
                      <Eye className="mr-1 h-4 w-4" /> View / Print
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openMicr(s)} data-testid={`micr-stub-${s.id}`}>
                      <Landmark className="mr-1 h-4 w-4" /> Add MICR
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(s)} data-testid={`delete-stub-${s.id}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="upload-stub-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Payroll Stubs</DialogTitle>
            <DialogDescription>Attach the combined pay-stub PDF for a pay date. It can be printed or downloaded whole.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Pay Date *</Label>
                <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="stub-pay-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Label (optional)</Label>
                <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Pay period 9/14–9/27" data-testid="stub-label" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Pay-stub PDF *</Label>
              <Input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="stub-file" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy || !file} data-testid="save-stub-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />} Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={micrOpen} onOpenChange={setMicrOpen}>
        <DialogContent data-testid="micr-stub-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add MICR Line to Checks</DialogTitle>
            <DialogDescription>
              Stamps a magnetic MICR line (check #, routing & account from Check Settings) onto the check portion at the top of each page — so you can print the checks on blank stock. Preview first, nudge the offsets to line it up, then generate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {micrAccts.length > 1 && (
              <div className="space-y-1.5">
                <Label>Bank Account</Label>
                <BankAccountSelect accounts={micrAccts} value={micrBankId} testid="micr-bank"
                  onChange={(id, a) => { setMicrBankId(id); setMicrStart(String(a?.current_check_number ?? 1001)); }} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Starting check number</Label>
              <Input type="number" min="1" value={micrStart} onChange={(e) => setMicrStart(e.target.value)} placeholder="from Check Settings" data-testid="micr-start" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Horizontal nudge (in)</Label>
                <Input type="number" step="0.05" value={micrDx} onChange={(e) => setMicrDx(e.target.value)} data-testid="micr-dx" />
              </div>
              <div className="space-y-1.5">
                <Label>Vertical nudge (in, + = down)</Label>
                <Input type="number" step="0.05" value={micrDy} onChange={(e) => setMicrDy(e.target.value)} data-testid="micr-dy" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Check numbers are sequential per page. "Generate &amp; Print" advances your Check Settings counter; "Preview" does not. Use MICR magnetic toner on blank check stock for bank-scannable checks.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => runMicr(false)} disabled={micrBusy} data-testid="micr-preview-btn">
              {micrBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />} Preview
            </Button>
            <Button onClick={() => runMicr(true)} disabled={micrBusy} data-testid="micr-generate-btn">
              {micrBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Printer className="mr-2 h-4 w-4" />} Generate &amp; Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
